/**
 * A real ticket as one Markdown file — everything needed to diagnose it.
 *
 * The playground export (lib/playground/transcript.ts) carries the
 * conversation, which was enough when the person asking had just typed it.
 * Pilot feedback is about REAL tickets (Deivydas, 2026-10-07): the file that
 * goes with "ops say this reply was wrong" must carry the whole working, or
 * the diagnosis starts with a round of screenshots. So beside the
 * conversation it carries, for every run, oldest first: routing, the
 * condition flags, what was established, the indeterminacies, the draft and
 * any refusal, the proposed writes and their actions, each tool call with
 * its arguments and result, the spend — and the raw facts blob the agent was
 * handed as "true, do not contradict".
 *
 * Built in the browser from the ticket detail the pane already loaded; no
 * endpoint knows this file exists.
 */

import { readRunFacts, readSpend, routingLabel } from './detail';

export interface ExportMessage {
  role: string;
  body: string;
  sentAt: string | null;
  createdAt?: string | null;
}

export interface ExportToolCall {
  tool: string;
  ok: boolean;
  latencyMs: number;
  args?: unknown;
  result?: unknown;
  errorCode?: string | null;
  confirmed?: boolean | null;
}

export interface ExportAction {
  type: string;
  status: string;
  amountCents: number | null;
  payload?: unknown;
  blockedReasons?: string[];
  approvedAt?: string | null;
  executedAt?: string | null;
}

export interface ExportRun {
  id: string;
  mode: string;
  status: string;
  escalated: boolean;
  escalationReason: string | null;
  confidence: number | null;
  workflowSlug: string | null;
  variantId: string | null;
  tags: string[];
  draftReply: string | null;
  facts: unknown;
  startedAt?: string | null;
  finishedAt?: string | null;
  toolCalls: ExportToolCall[];
  actions?: ExportAction[];
}

export interface ExportTicket {
  id: string;
  externalId: string;
  intent: string | null;
  status: string;
  customerName: string | null;
  customerEmail: string | null;
  openedAt?: string | null;
  brand: { slug: string; name: string };
  messages: ExportMessage[];
  /** As the detail endpoint returns them: newest first. */
  runs: ExportRun[];
  priorTickets: number | null;
}

/** A tool result longer than this is cut — a customer snapshot can run to pages. */
const RESULT_LIMIT = 6_000;

export function buildTicketMarkdown(ticket: ExportTicket, exportedBy?: string | null): string {
  const runs = [...ticket.runs].reverse();
  const lines: string[] = [
    `# Ticket #${ticket.externalId} — ${ticket.brand.name}`,
    '',
    `- **Zendesk ticket**: #${ticket.externalId}`,
    `- **Brand**: ${ticket.brand.name} (\`${ticket.brand.slug}\`)`,
    `- **Intent**: ${ticket.intent ?? 'none recorded'}`,
    `- **Status in Zoye**: ${ticket.status}`,
    `- **Customer**: ${[ticket.customerName, ticket.customerEmail].filter(Boolean).join(' · ') || 'unknown'}`,
    ...(ticket.priorTickets !== null ? [`- **Prior tickets from this customer**: ${ticket.priorTickets}`] : []),
    ...(ticket.openedAt ? [`- **Opened**: ${stamp(ticket.openedAt)}`] : []),
    `- **Runs**: ${runs.length}`,
    `- **Internal id**: \`${ticket.id}\``,
    ...(exportedBy ? [`- **Exported by**: ${exportedBy}`] : []),
    `- **Exported**: ${new Date().toLocaleString()}`,
    '',
    '> A live ticket. An AI message marked "sent" reached the customer through Zendesk; one marked "not sent" was drafted, held, refused or dry-run.',
    '',
    '## Conversation',
    '',
  ];

  if (ticket.messages.length === 0) lines.push('_No messages stored._', '');
  for (const message of ticket.messages) {
    const when = message.sentAt ?? message.createdAt ?? null;
    const delivery = message.role === 'customer' ? '' : message.sentAt ? ' · sent' : ' · not sent';
    const who = message.role === 'system' ? 'staff (from Zendesk)' : message.role;
    lines.push(`**${who}**${when ? ` · ${stamp(when)}` : ''}${message.role === 'system' ? '' : delivery}`, '', quote(message.body), '');
  }

  runs.forEach((run, index) => lines.push(...runSection(run, index + 1, runs.length)));
  return lines.join('\n');
}

function runSection(run: ExportRun, number: number, total: number): string[] {
  const facts = readRunFacts(run.facts);
  const spend = readSpend(run.facts);
  const lines: string[] = [
    `## Run ${number} of ${total}${number === total ? ' (latest)' : ''}`,
    '',
    ...(run.startedAt ? [`- **Started**: ${stamp(run.startedAt)}${run.finishedAt ? ` · finished ${stamp(run.finishedAt)}` : ''}`] : []),
    `- **Mode**: ${run.mode} · **status**: ${run.status}${run.escalated ? ` · **escalated**: ${run.escalationReason ?? 'no reason recorded'}` : ''}`,
    `- **Routing**: ${routingLabel(run.workflowSlug, run.facts)}${run.variantId ? ` · ${run.variantId}` : ''}${run.confidence !== null ? ` · confidence ${run.confidence.toFixed(2)}` : ''}`,
    ...(run.tags.length > 0 ? [`- **Tags**: ${run.tags.join(', ')}`] : []),
    ...(facts.stopReason ? [`- **Stop reason**: ${facts.stopReason}`] : []),
    ...(facts.rounds !== null ? [`- **Agent rounds**: ${facts.rounds}`] : []),
    `- **Run id**: \`${run.id}\``,
    '',
  ];

  const conditions = conditionsOf(run.facts);
  if (conditions.length > 0) {
    const on = conditions.filter(([, value]) => value).map(([name]) => name);
    const off = conditions.filter(([, value]) => !value).map(([name]) => name);
    lines.push('### Conditions', '', `- **true**: ${on.join(', ') || 'none'}`, `- **false**: ${off.join(', ') || 'none'}`, '');
  }

  lines.push('### What was established', '');
  if (facts.rows.length === 0) lines.push('_Nothing was established._');
  for (const row of facts.rows) lines.push(`- **${row.label}**: ${row.value}`);
  lines.push('');

  if (facts.indeterminate.length > 0) {
    lines.push('### Could not be determined', '', ...facts.indeterminate.map((reason) => `- ${reason}`), '');
  }

  lines.push('### Draft', '');
  lines.push(run.draftReply ? quote(run.draftReply) : '_No draft._', '');

  if (facts.violations.length > 0) {
    lines.push('### Refused by bot rules', '');
    for (const v of facts.violations) {
      lines.push(`- **${v.ruleId}** (${v.severity}): "${v.evidence}" — ${v.explanation}`);
    }
    lines.push('');
  }

  if (facts.proposedWrites.length > 0) {
    lines.push('### Proposed writes', '');
    for (const write of facts.proposedWrites) {
      lines.push(`- \`${write.tool}\` (round ${write.round}): \`${JSON.stringify(write.arguments)}\``);
    }
    lines.push('');
  }

  if (run.actions && run.actions.length > 0) {
    lines.push('### Actions', '');
    for (const action of run.actions) {
      const amount = action.amountCents !== null ? ` · $${(action.amountCents / 100).toFixed(2)}` : '';
      const blocked = action.blockedReasons?.length ? ` · blocked: ${action.blockedReasons.join('; ')}` : '';
      const executed = action.executedAt ? ` · executed ${stamp(action.executedAt)}` : '';
      lines.push(`- **${action.type}** — ${action.status}${amount}${blocked}${executed}`);
      if (action.payload !== undefined) lines.push(`  - payload: \`${JSON.stringify(action.payload)}\``);
    }
    lines.push('');
  }

  lines.push('### Tool calls', '');
  if (run.toolCalls.length === 0) lines.push('_No tools were called._', '');
  run.toolCalls.forEach((call, index) => {
    const outcome = call.ok ? `ok · ${call.latencyMs}ms` : `FAILED${call.errorCode ? ` (${call.errorCode})` : ''} · ${call.latencyMs}ms`;
    const confirmed = call.confirmed === true ? ' · confirmed' : call.confirmed === false ? ' · NOT confirmed' : '';
    lines.push(`${index + 1}. \`${call.tool}\` — ${outcome}${confirmed}`, '');
    if (call.args !== undefined) lines.push('   args:', '', fence(json(call.args), '   '), '');
    if (call.result !== undefined && call.result !== null) lines.push('   result:', '', fence(json(call.result, RESULT_LIMIT), '   '), '');
  });

  if (spend) {
    lines.push('### Spend', '');
    const total = spend.costMicroUsd !== null ? `$${(spend.costMicroUsd / 1_000_000).toFixed(4)}` : 'unpriced';
    lines.push(`- **Total**: ${total} · ${spend.inputTokens} in / ${spend.outputTokens} out · ${spend.latencyMs}ms`);
    for (const role of spend.perRole) {
      const cost = role.costMicroUsd !== null ? `$${(role.costMicroUsd / 1_000_000).toFixed(4)}` : 'unpriced';
      lines.push(`- ${role.role}: ${role.inputTokens} in / ${role.outputTokens} out · ${cost} · ${role.latencyMs}ms`);
    }
    lines.push('');
  }

  lines.push('### Raw facts', '', '<details><summary>facts JSON — what the agent was given as true</summary>', '', fence(json(run.facts)), '', '</details>', '');
  return lines;
}

function conditionsOf(raw: unknown): Array<[string, boolean]> {
  const conditions = (raw as Record<string, unknown> | null)?.conditions;
  return typeof conditions === 'object' && conditions !== null && !Array.isArray(conditions)
    ? Object.entries(conditions as Record<string, unknown>).map(([k, v]) => [k, v === true])
    : [];
}

function json(value: unknown, limit?: number): string {
  let text: string;
  try {
    text = JSON.stringify(value, null, 2) ?? 'null';
  } catch {
    text = String(value);
  }
  return limit !== undefined && text.length > limit
    ? `${text.slice(0, limit)}\n… (${text.length - limit} more characters cut)`
    : text;
}

function fence(text: string, indent = ''): string {
  // A longer fence than any backtick run inside, so a result can never close it early.
  const longest = Math.max(2, ...(text.match(/`+/g) ?? []).map((run) => run.length));
  const marks = '`'.repeat(longest + 1);
  return [`${marks}json`, ...text.split('\n'), marks].map((line) => `${indent}${line}`).join('\n');
}

function quote(text: string): string {
  return text
    .split('\n')
    .map((line) => `> ${line}`)
    .join('\n');
}

function stamp(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

/** `ticket-1908921-american-vitality-2026-10-07.md` — the Zendesk number leads. */
export function ticketExportFilename(ticket: Pick<ExportTicket, 'externalId' | 'brand' | 'openedAt'>): string {
  const brand = ticket.brand.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const day = (ticket.openedAt ? new Date(ticket.openedAt) : new Date()).toISOString().slice(0, 10);
  return `ticket-${ticket.externalId}-${brand}-${day}.md`;
}
